import { useState, useEffect } from "react";
import useFeaturesStore from "@/store/featuresStore";

interface QueueTimeResult {
  automaticDispatchesEnabled: boolean;
  automaticQueueTimeEnabled: boolean;
  automaticThroughputEnabled: boolean;
  closingTime: string;
  closureReason: string | null;
  defaultSeats: number;
  defaultUnits: number;
  id: number;
  lastSync: string | null;
  latestQueueTime: number;
  latestQueueTimeRegisteredAt: string | null;
  latestSeats: number;
  latestSeatsRegisteredAt: string | null;
  latestSignedInUsers: number;
  latestThroughput: number;
  latestThroughputRegisteredAt: string | null;
  latestUnits: number;
  latestUnitsRegisteredAt: string | null;
  locationType: "Rides" | "Shows" | "Attractions" | "Restaurants" | string;
  maxSeats: number;
  maxThroughput: number;
  metrics: []; // Update if you have a more specific type for metrics
  name: string;
  online: boolean;
  openedAt: string | null;
  openingTime: string;
  outOfService: boolean;
  secondaryReason: string | null;
  statusOpen: boolean;
  targetDispatchHour: number;
  targetThroughput: number;
  unableToOpenReason: string | null;
  unitCounting: boolean;
}

export interface QueueTime {
  value: number;
  name: string;
  amenityId: string;
  queueAmenityId?: string;
  featureId?: string;
  attractionId?: number;
  rideId: number;
  closingTime?: string;
  openingTime?: string;
  online?: boolean;
  outOfService?: boolean;
  statusOpen?: boolean;
}

export enum FetchStatus {
  Loading = "loading",
  Loaded = "loaded",
  Error = "error",
}

export default function useQueueTimes() {
  const apiURL = import.meta.env.VITE_QUEUETIME_API;
  const apiToken = import.meta.env.VITE_PROXIMIIO_TOKEN;
  const [data, setData] = useState<QueueTime[]>([]);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>(
    FetchStatus.Loading,
  );

  const features = useFeaturesStore((state) => state.features);

  useEffect(() => {
    const queueAmenities = [
      {
        id: "2ceebcfb-0db3-483b-8798-0f8e720167e8:3a3f11ba-c3b1-4675-a1ae-6ac949cfb573",
        minValue: 0,
        maxValue: 4,
      },
      {
        id: "2ceebcfb-0db3-483b-8798-0f8e720167e8:39370efe-eeab-4c4a-8570-feb957669c3d",
        minValue: 5,
        maxValue: 14,
      },
      {
        id: "2ceebcfb-0db3-483b-8798-0f8e720167e8:f2d602f8-9e94-4ef4-a901-067879892520",
        minValue: 15,
        maxValue: 30,
      },
      {
        id: "2ceebcfb-0db3-483b-8798-0f8e720167e8:9095d4bc-7f79-43f6-ad42-d9a2a4cc44e6",
        minValue: 31,
        maxValue: 1000,
      },
    ];

    const filteredFeatures = features.filter((f) => {
      return (
        f.geometry.type === "Point" &&
        (f.properties.amenity ===
          "2ceebcfb-0db3-483b-8798-0f8e720167e8:d6597c1e-2ee4-4dbf-a4db-31c9bb9dd1b3" ||
          f.properties.amenity ===
            "2ceebcfb-0db3-483b-8798-0f8e720167e8:5d4de5ec-7dc9-4bc5-9e48-c966fe39f9ab" ||
          f.properties.amenity ===
            "2ceebcfb-0db3-483b-8798-0f8e720167e8:db08735f-4c53-449a-9ab9-eebe23165b57")
      );
    });

    const fetchQueueTimes = async () => {
      try {
        // Set loading state
        setFetchStatus(FetchStatus.Loading);
        const res = await fetch(`${apiURL}`, {
          headers: {
            Authorization: `Bearer ${apiToken}`,
          },
        });
        const data: QueueTimeResult[] = await res.json();
        const queueTimes: QueueTime[] = data.map((item) => {
          const queueAmenityId = queueAmenities.find(
            (i) =>
              i.minValue <= item.latestQueueTime &&
              i.maxValue >= item.latestQueueTime,
          )?.id;
          const rideFeature = filteredFeatures.find(
            (f) => Number(f.properties.metadata.cmdCentrId) === item.id,
          );
          return {
            name: item.name,
            value: item.latestQueueTime,
            closingTime: item.closingTime,
            openingTime: item.openingTime,
            online: item.online,
            outOfService: item.outOfService,
            statusOpen: item.statusOpen,
            amenityId: rideFeature?.properties.amenity,
            featureId: rideFeature?.id,
            rideId: item.id,
            queueAmenityId,
          };
        });
        setData(queueTimes);
        setFetchStatus(FetchStatus.Loaded);
      } catch (error) {
        console.error("Error fetching queue times data:", error);
        // Set error state
        setFetchStatus(FetchStatus.Error);
      }
    };

    if (features && features.length > 0) {
      fetchQueueTimes();
      setInterval(() => {
        fetchQueueTimes();
      }, 60e3 * 5);
    }
  }, [apiURL, apiToken, features]);

  return [data, fetchStatus];
}
